<template>
  <v-row>
    <!-- LEFT: FORM -->
    <v-col cols="12" md="6">
      <v-card
        flat
        class="inv-form-card d-flex flex-column"
        height="calc(100vh - 82px)"
      >
        <!-- HEADER BAND -->
        <div class="inv-form-header pl-base-bg">
          <div class="inv-form-header-left">
            <div class="inv-form-header-icon">
              <v-icon color="#fff" size="22">mdi-file-document-outline</v-icon>
            </div>
            <div>
              <div class="inv-form-title">Create Invoice</div>
              <div class="inv-form-subtitle">Fill in the details to generate a new invoice</div>
            </div>
          </div>

          <v-btn class="inv-demo-btn" small @click="openDemo">
            <v-icon left size="16">mdi-play-circle-outline</v-icon>
            View Demo
          </v-btn>
        </div>

        <v-alert
          v-if="sourceQuotationNumber"
          type="info"
          outlined
          dense
          border="left"
          class="mx-6 mt-4 mb-0"
        >
          Converting Quotation #{{ sourceQuotationNumber }} to an invoice.
        </v-alert>

        <v-form ref="form" v-model="valid" class="pa-6 overflow-y-auto flex-grow-1">
          <v-row dense>
            <!-- INVOICE NUMBER -->
            <v-col :cols="isMobile ? 12 : 4">
              <v-text-field
                v-model="form.invoiceNumber"
                label="Invoice Number"
                outlined
                dense
                prepend-inner-icon="mdi-pound"
                :rules="[rules.required, rules.uniqueInvoice]"
              />
            </v-col>

            <!-- INVOICE DATE -->
            <v-col :cols="isMobile ? 12 : 4">
              <v-menu
                v-model="invoiceMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formatDate(form.invoiceDate)"
                    label="Invoice Date"
                    readonly
                    outlined
                    dense
                    prepend-inner-icon="mdi-calendar"
                    :rules="[rules.required]"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>

                <v-date-picker
                  v-model="form.invoiceDate"
                  @change="invoiceMenu = false"
                />
              </v-menu>
            </v-col>

            <!-- DUE DATE -->
            <v-col :cols="isMobile ? 12 : 4">
              <v-menu
                v-model="dueMenu"
                :close-on-content-click="false"
                offset-y
                left
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formatDate(form.dueDate)"
                    label="Due Date"
                    readonly
                    outlined
                    dense
                    prepend-inner-icon="mdi-calendar"
                    append-icon="mdi-close-circle"
                    @click:append="form.dueDate = null"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>

                <v-date-picker
                  v-model="form.dueDate"
                  @change="dueMenu = false"
                />
              </v-menu>
            </v-col>

            <v-col :cols="isMobile ? 12 : 6">
              <v-autocomplete
                v-model="selectedCustomer"
                :items="customers"
                item-text="name"
                item-value="_id"
                label="Select Customer"
                outlined
                dense
                prepend-inner-icon="mdi-account"
                return-object
                @change="onCustomerSelect"
              >
                <!-- ADD ICON + TOOLTIP -->
                <template v-slot:append>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        color="primary"
                        style="cursor: pointer"
                        @click="customerDialog = true"
                      >
                        mdi-plus
                      </v-icon>
                    </template>
                    <span>New Customer</span>
                  </v-tooltip>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col :cols="isMobile ? 12 : 6">
              <v-text-field
                v-model="form.customer.address"
                label="Customer Address"
                outlined
                dense
                prepend-inner-icon="mdi-map-marker"
              />
            </v-col>

            <v-col :cols="isMobile ? 12 : 6">
              <v-text-field
                v-model="form.customer.gstin"
                label="Customer GSTIN"
                outlined
                dense
                prepend-inner-icon="mdi-card-account-details"
              />
            </v-col>

            <v-col :cols="isMobile ? 12 : 6">
              <v-text-field
                v-model="form.placeOfSupply"
                label="Place of Supply (State)"
                outlined
                dense
                prepend-inner-icon="mdi-map-marker-radius"
              />
            </v-col>

            <v-col cols="12">
              <div class="inv-section-label">Items</div>

              <div
                v-for="(item, index) in form.items"
                :key="index"
                class="inv-item-row"
              >
                <v-btn
                  icon
                  class="inv-item-remove"
                  @click="removeItem(index)"
                >
                  <v-icon color="error" size="24">mdi-delete-outline</v-icon>
                </v-btn>

                <v-row dense>
                  <v-col :cols="isMobile ? 12 : 4">
                    <v-autocomplete
                      v-model="item.productObj"
                      :items="products"
                      item-text="productName"
                      return-object
                      label="Product"
                      outlined
                      dense
                      hide-details="auto"
                      :error="submitAttempted && !item.product"
                      :error-messages="submitAttempted && !item.product ? 'Select a product from the list' : []"
                      prepend-inner-icon="mdi-cube-outline"
                      @change="onProductSelect(item, index)"
                    >
                      <!-- ADD NEW PRODUCT -->
                      <template v-slot:append>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              color="primary"
                              style="cursor: pointer"
                              @click="openProductDialog(index)"
                            >
                              mdi-plus
                            </v-icon>
                          </template>
                          <span>New Product</span>
                        </v-tooltip>
                      </template>
                    </v-autocomplete>
                  </v-col>

                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      v-model="item.hsn"
                      label="HSN/SAC"
                      dense
                      outlined
                      hide-details
                      hint="4-8 digit HSN/SAC code"
                    />
                  </v-col>

                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      v-model.number="item.quantity"
                      type="number"
                      label="Qty"
                      dense
                      outlined
                      :hint="stockHint(item)"
                      :persistent-hint="!!stockHint(item)"
                      :class="{ 'inv-qty-warning': isOverStock(item) }"
                      @input="calcItem(index)"
                    />
                  </v-col>
                </v-row>

                <v-row dense class="mt-1">
                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      v-model.number="item.price"
                      type="number"
                      label="Rate"
                      dense
                      outlined
                      hide-details
                      @input="calcItem(index)"
                    />
                  </v-col>

                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      v-model.number="item.taxPercent"
                      type="number"
                      label="Tax %"
                      dense
                      outlined
                      hide-details
                      @input="calcItem(index)"
                    />
                  </v-col>

                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      :value="formatAmount(item.amount)"
                      label="Amount"
                      dense
                      outlined
                      readonly
                      hide-details
                    />
                  </v-col>
                </v-row>
              </div>

              <v-btn small class="inv-add-item-btn" @click="addItem">
                <v-icon left size="16">mdi-plus</v-icon>
                Add Product
              </v-btn>
            </v-col>

            <!-- TOTALS -->
            <v-col cols="12">
              <v-divider class="my-4"></v-divider>

              <div class="totals-grid">
                <div>Subtotal</div>
                <div>₹ {{ formatAmount(subtotal) }}</div>

                <div>GST</div>
                <div>₹ {{ formatAmount(gst) }}</div>
              </div>

              <div class="total-row mt-2">
                <span>Total</span>
                <strong>₹ {{ formatAmount(totalAmount) }}</strong>
              </div>
            </v-col>

            <!-- NOTE -->
            <v-col cols="12">
              <v-textarea
                v-model="form.note"
                label="Note"
                outlined
                dense
                rows="1"
                prepend-inner-icon="mdi-note-text"
              />
            </v-col>
          </v-row>
        </v-form>

        <!-- ACTION -->
        <v-row class="inv-form-actions px-6 flex-grow-0 flex-shrink-0" justify="end" dense>
          <v-col cols="6" sm="auto">
            <v-btn block @click="reset" color="secondary">Reset</v-btn>
          </v-col>

          <v-col v-if="!isViewOnly" cols="6" sm="auto">
            <v-btn
              block
              class="inv-save-btn"
              :loading="loading"
              @click="submit"
              :disabled="
                loading ||
                totalAmount <= 0 ||
                !form.customer.name ||
                !form.invoiceNumber ||
                !pdfGenerated ||
                hasEmptyProduct
              "
            >
              Save
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-col>

    <!-- RIGHT PREVIEW -->
    <v-col cols="12" md="6">
      <v-card
        class="inv-preview-card d-flex flex-column"
        flat
        height="calc(100vh - 82px)"
      >
        <div class="inv-preview-header">
          <v-icon color="#0a5a90" class="mr-2">mdi-file-eye-outline</v-icon>
          <h3 class="inv-preview-title">Document Preview</h3>
        </div>

        <v-row class="px-4 flex-grow-0 flex-shrink-0" no-gutters>
          <v-col cols="12">
            <v-btn
              block
              class="inv-generate-btn"
              @click="autoGenerateInvoice"
              :disabled="
                totalAmount <= 0 || !form.customer.name || !form.invoiceNumber || hasEmptyProduct
              "
            >
              Generate Invoice
            </v-btn>
            <div v-if="!pdfGenerated" class="mt-2 text-caption" style="color: #b71c1c">
              Generate the invoice PDF before saving.
            </div>
          </v-col>
        </v-row>

        <!-- PREVIEW -->
        <div class="viewer mt-4 mx-4">
          <!-- IMAGE PREVIEW -->
          <img v-if="preview && isImage" :src="preview" class="preview-img" />

          <!-- PDF PREVIEW -->
          <iframe
            v-else-if="preview"
            :src="preview"
            class="preview-frame"
          ></iframe>

          <!-- EMPTY STATE -->
          <div v-else class="empty-state">
            <v-icon size="40" color="#cbd5e1">mdi-file-document-outline</v-icon>
            <div class="mt-2">No document selected</div>
          </div>
        </div>
      </v-card>
    </v-col>

    <GlobalDialog v-model="customerDialog" title="New Customer" :width="700">
      <CreateCustomer mode="dialog" @created="onCustomerCreated" />
    </GlobalDialog>

    <GlobalDialog v-model="productDialog" title="New Product" :width="600">
      <CreateProduct mode="dialog" @created="onProductCreated" />
    </GlobalDialog>
  </v-row>
</template>

<script>
import { mapActions } from "vuex";
import GlobalDialog from "./common/global-dialog.vue";
import CreateCustomer from "./create-customer.vue";
import CreateProduct from "./create-product.vue";
import {
  resolveSellerState,
  resolvePlaceOfSupplyState,
  splitTax,
  numberToWords,
  formatINR,
} from "../utils/gst";
import { PDF_COLORS, makePdfPrimitives } from "../utils/pdf-helpers";

export default {
  name: "CreateInvoice",
  components: {
    GlobalDialog,
    CreateCustomer,
    CreateProduct,
  },

  async created() {
    this.fetchInvoices();
    this.fetchProducts();

    await this.fetchCustomers();

    const fromQuotation = this.$route.query.fromQuotation;
    if (fromQuotation) {
      await this.prefillFromQuotation(fromQuotation);
      return;
    }

    this.fetchInvoiceNumber();

    const customerId = this.$route.query.customerId;
    if (customerId) {
      const customer = this.customers.find((c) => c._id === customerId);
      if (customer) {
        this.selectedCustomer = customer;
        this.onCustomerSelect(customer);
      }
    }
  },

  data() {
    const getISTDate = () => {
      return new Date().toLocaleDateString("en-CA", {
        timeZone: "Asia/Kolkata",
      });
    };
    return {
      valid: false,
      loading: false,
      file: null,
      preview: null,
      pdfGenerated: false,
      submitAttempted: false,

      invoiceMenu: false,
      dueMenu: false,
      selectedCustomer: null,
      customerDialog: false,
      productDialog: false,

      sourceQuotationId: null,
      sourceQuotationNumber: null,

      form: {
        invoiceNumber: "",
        invoiceDate: getISTDate(),
        dueDate: getISTDate(),
        placeOfSupply: "",

        customer: {
          customerId: null,
          name: "",
          address: "",
          gstin: "",
          state: "",
        },

        items: [
          {
            productId: null,
            product: "",
            quantity: 1,
            hsn: "",
            price: 0,
            taxPercent: 18,
            amount: 0,
            availableStock: null,
          },
        ],

        note: "",
      },

      rules: {
        required: (v) => !!v || "Required",
        uniqueInvoice: (v) => {
          if (!v || !this.invoices.length) return true;
          return (
            !this.invoices.some(
              (i) => i.invoiceNumber?.toLowerCase() === v.toLowerCase(),
            ) || "Invoice number already exists"
          );
        },
      },
    };
  },

  watch: {
    "form.items": {
      deep: true,
      handler() {
        this.form.items.forEach((_, i) => this.calcItem(i));
      },
    },
    form: {
      deep: true,
      handler() {
        this.pdfGenerated = false;
      },
    },
  },

  computed: {
    invoices() {
      return this.$store.state.invoices.list || [];
    },

    subtotal() {
      return this.form.items.reduce((sum, item) => sum + (item.amount || 0), 0);
    },

    gst() {
      return this.form.items.reduce((sum, item) => {
        const taxPercent = Math.min(28, Math.max(0, item.taxPercent || 0));
        return sum + ((item.amount || 0) * taxPercent) / 100;
      }, 0);
    },

    totalAmount() {
      return this.subtotal + this.gst;
    },

    hasEmptyProduct() {
      return this.form.items.some((item) => !item.product);
    },

    sellerState() {
      return resolveSellerState({
        sellerGstin: this.profile?.gstin,
        sellerState: this.profile?.state,
      });
    },

    placeOfSupplyState() {
      return resolvePlaceOfSupplyState({
        customerGstin: this.form.customer.gstin,
        customerState: this.form.customer.state,
        explicitPlaceOfSupply: this.form.placeOfSupply,
      });
    },

    taxSplit() {
      return this.form.items.reduce(
        (acc, item) => {
          const { isInterState, cgst, sgst, igst } = splitTax({
            sellerState: this.sellerState,
            placeOfSupplyState: this.placeOfSupplyState,
            taxableAmount: item.amount || 0,
            taxPercent: Math.min(28, Math.max(0, item.taxPercent || 0)),
          });
          acc.isInterState = isInterState;
          acc.cgst += cgst;
          acc.sgst += sgst;
          acc.igst += igst;
          return acc;
        },
        { isInterState: false, cgst: 0, sgst: 0, igst: 0 },
      );
    },

    isImage() {
      return this.file && this.file.type.startsWith("image/");
    },

    profile() {
      return this.$store.state.business?.profile;
    },

    customers() {
      return this.$store.state.customers.customers;
    },

    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    terms() {
      return this.$store.state.terms.terms;
    },

    bankAccount() {
      return this.$store.state.bank.account;
    },

    products() {
      return this.$store.state.products.products;
    },
  },

  methods: {
    ...mapActions("business", ["getProfile"]),
    openDemo() {
      window.open(
        "https://profitledgex-documents.nyc3.cdn.digitaloceanspaces.com/Demo's/Create%20Invoice.mkv",
        "_blank",
      );
    },

    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return `${String(d.getDate()).padStart(2, "0")}/${String(
        d.getMonth() + 1,
      ).padStart(2, "0")}/${d.getFullYear()}`;
    },

    formatAmount(val) {
      return Number(val || 0).toFixed(2);
    },

    onCustomerSelect(customer) {
      if (!customer) return;

      const gstin = customer.gstin || "";

      Object.assign(this.form.customer, {
        customerId: customer._id || null,
        name: customer.name || "",
        gstin: customer.gstin || "",
        state: customer.state || "",
        address: [
          customer.address,
          customer.city,
          customer.state,
          customer.pincode,
          customer.country,
        ]
          .filter(Boolean)
          .join(", "),
      });

      this.form.placeOfSupply = resolvePlaceOfSupplyState({
        customerGstin: gstin,
        customerState: customer.state,
      }) || "";
    },

    addItem() {
      this.form.items.push({
        productId: null,
        product: "",
        quantity: 1,
        hsn: "",
        price: 0,
        taxPercent: 18,
        amount: 0,
        availableStock: null,
      });
    },

    removeItem(i) {
      if (this.form.items.length === 1) return;
      this.form.items.splice(i, 1);
    },

    calcItem(i) {
      const item = this.form.items[i];
      if (!item) return;

      const qty = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;

      item.amount = +(qty * price).toFixed(2);
    },

    async prefillFromQuotation(quotationId) {
      try {
        const res = await this.$store.dispatch(
          "quotations/getQuotationById",
          quotationId,
        );
        const q = res.quotation;

        if (q.status === "CONVERTED") {
          this.$toast.error("This quotation has already been converted to an invoice");
          this.$router.push(`/quotations/${q._id}/view`);
          return;
        }

        this.sourceQuotationId = q._id;
        this.sourceQuotationNumber = q.quotationNumber;

        this.form.placeOfSupply = q.placeOfSupply || "";
        this.form.note = q.note || "";
        this.form.dueDate = null;

        Object.assign(this.form.customer, {
          customerId: q.customer?.customerId || null,
          name: q.customer?.name || "",
          address: q.customer?.address || "",
          gstin: q.customer?.gstin || "",
          state: q.customer?.state || "",
        });

        if (q.customer?.customerId) {
          const customer = this.customers.find((c) => c._id === q.customer.customerId);
          if (customer) {
            this.selectedCustomer = customer;
            this.onCustomerSelect(customer);
          }
        }

        this.form.items = (q.items || []).map((it) => ({
          productId: it.productId || null,
          product: it.product || "",
          hsn: it.hsn || "",
          quantity: it.quantity,
          price: it.price,
          taxPercent: it.taxPercent || 0,
          amount: it.amount,
          availableStock: null,
          // Seeds the Product autocomplete's display value from the
          // quotation's snapshot — same reasoning as edit-invoice.vue's
          // loadInvoice(). Without this the dropdown renders blank for
          // every row despite product/hsn/price being populated.
          productObj: it.product ? { productName: it.product, _id: it.productId } : null,
        }));

        await this.fetchInvoiceNumber();
      } catch (err) {
        this.$toast.error("Failed to load quotation");
      }
    },

    async submit() {
      this.submitAttempted = true;
      if (!this.$refs.form.validate()) return;
      if (this.loading) return;

      this.loading = true;

      try {
        let fileBase64 = null;
        if (this.file) fileBase64 = await this.toBase64(this.file);

        const payload = {
          ...this.form,
          subtotal: this.subtotal,
          gst: this.gst,
          cgst: this.taxSplit.cgst,
          sgst: this.taxSplit.sgst,
          igst: this.taxSplit.igst,
          isInterState: this.taxSplit.isInterState,
          totalAmount: this.totalAmount,
          file: fileBase64,
          fileName: this.file?.name,
          fileType: this.file?.type,
        };

        const result = this.sourceQuotationId
          ? await this.$store.dispatch("quotations/convertToInvoice", {
              id: this.sourceQuotationId,
              payload,
            })
          : await this.$store.dispatch("invoices/CreateInvoice", payload);

        this.$toast.success(this.sourceQuotationId ? "Quotation converted to invoice" : "Created");

        (result?.stockWarnings || []).forEach((w) => {
          this.$toast.warning(
            `${w.productName} stock is now ${w.resultingQuantity} (below zero)`,
          );
        });

        this.reset();

        if (result && result.invoice) {
          this.$router.push(`/invoices/${result.invoice._id}/view`);
        }
      } catch (e) {
        console.error(e);
        this.$toast.error("Error");
      } finally {
        this.loading = false;
      }
    },

    reset() {
      this.$refs.form.reset();
      this.file = null;
      this.preview = null;
      this.pdfGenerated = false;
      this.submitAttempted = false;
      this.invoiceMenu = false;
      this.dueMenu = false;

      this.form.items = [
        {
          productId: null,
          product: "",
          quantity: 1,
          hsn: "",
          price: 0,
          taxPercent: 18,
          amount: 0,
          availableStock: null,
        },
      ];
    },

    toBase64(file) {
      return new Promise((res, rej) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
      });
    },

    async fetchInvoices() {
      this.loading = true;
      try {
        await this.$store.dispatch("invoices/getInvoices");
      } finally {
        this.loading = false;
      }
    },

    async fetchCustomers() {
      this.loading = true;
      try {
        await this.$store.dispatch("customers/getCustomers");
      } finally {
        this.loading = false;
      }
    },

    async fetchProducts() {
      this.loading = true;
      try {
        await this.$store.dispatch("products/getProducts");
      } finally {
        this.loading = false;
      }
    },

    async fetchInvoiceNumber() {
      try {
        const num = await this.$store.dispatch("invoices/getNextInvoiceNumber");
        this.form.invoiceNumber = num;
      } catch (e) {
        console.error("Error fetching invoice number", e);
      }
    },

    formatINR(value) {
      return formatINR(value);
    },

    wrapText(doc, text, x, y, maxWidth, lineHeight = 5) {
      const lines = doc.splitTextToSize(text || "-", maxWidth);
      lines.forEach((line, i) => {
        doc.text(line, x, y + i * lineHeight);
      });
      return lines.length * lineHeight;
    },

    numberToWords(num) {
      return numberToWords(num);
    },

    async autoGenerateInvoice() {
      this.submitAttempted = true;
      try {
        await Promise.all([
          this.getProfile(),
          this.$store.dispatch("terms/getTerms"),
          this.$store.dispatch("bank/getAccount"),
        ]);

        const customer = this.form.customer;
        const bank     = this.bankAccount;
        const termsRaw = this.$store.state.terms?.terms?.content || "Goods once sold will not be taken back.\nPay on time.";

        if (!this.profile || !customer) {
          this.$toast.error("Seller or Customer missing");
          return;
        }

        const seller = {
          ...this.profile,
          address: [
            this.profile.address,
            this.profile.city,
            this.profile.state,
            this.profile.pincode,
            this.profile.country,
          ]
            .filter(Boolean)
            .join(", "),
        };

        const { jsPDF } = await import("jspdf");
        const autoTable = (await import("jspdf-autotable")).default;
        const doc       = new jsPDF({ unit: "mm", format: "a4" });
        const pageW     = doc.internal.pageSize.width;
        const pageH     = doc.internal.pageSize.height;
        const M         = 0;
        const CW        = pageW;
        const PAD       = 5;
        const LH        = 4.5;
        const FOOTER_H  = 10;

        // ── colours + primitives — shared with Quotation/Credit-Debit
        // Note PDF generation, see www/src/utils/pdf-helpers.js ──────────
        const {
          NAVY, BLUE, MUTED, BODY, LBODY, WHITE, DARK,
          BGROW, BGALT, BRDR, BLIGHT, WBLU, WBDR,
        } = PDF_COLORS;
        const { sc, rc, fr, sr, hl, rl, wt, fmtDate } = makePdfPrimitives(doc);

        const sameState = !this.taxSplit.isInterState;

        const termEntries = termsRaw
          .split(/\n/)
          .map(l => l.trim().replace(/^\d+[.)]\s*/, ""))
          .filter(Boolean);

        let y = M;

        // ══════════════════════════════════════════════════════════════
        // 1. HEADER  — dark navy, company name + contact left, badge right
        // ══════════════════════════════════════════════════════════════
        const badgeW  = 52;
        const badgeH  = 12;
        const badgeX  = M + CW - badgeW;
        const badgeY  = y;
        const leftW   = badgeX - M - PAD - 4;   // text area to the left of badge
        const addrWrap    = wt(seller.address || "", leftW);
        const contactStr  = [seller.phone || "", seller.email || ""].filter(Boolean).join("   ·   ");
        const contactWrap = wt(contactStr, leftW);
        const headerH     = Math.max(32,
          8 + 7 + 3 + addrWrap.length * 3.8 + 2 + contactWrap.length * 3.8 + 4
        );

        fr(M, y, CW, headerH, NAVY);

        doc.setFontSize(16); doc.setFont("helvetica", "bold"); sc(...WHITE);
        doc.text(seller.businessName || "Company Name", M + PAD + 2, y + 10);

        let hSubY = y + 16;
        hSubY = rl(addrWrap,    M + PAD + 2, hSubY, 7.5, "normal", [169, 183, 208], 3.8);
        hSubY += 2;
        rl(contactWrap, M + PAD + 2, hSubY, 7.5, "normal", [169, 183, 208], 3.8);

        // badge
        fr(badgeX, badgeY, badgeW, badgeH, BLUE);
        doc.setFontSize(9); doc.setFont("helvetica", "bold"); sc(...WHITE);
        doc.text("TAX INVOICE", badgeX + badgeW / 2, badgeY + badgeH / 2 + 1.8, { align: "center", charSpace: 0.8 });

        // GSTIN on one line below badge
        if (seller.gstin) {
          doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); sc(169, 183, 208);
          doc.text(`GSTIN  ${seller.gstin}`, M + CW - 2, badgeY + badgeH + 6, { align: "right" });
        }
        rc();
        y += headerH;

        // ══════════════════════════════════════════════════════════════
        // 2. META ROW  — light grey bar, 4 columns, no outer border
        // ══════════════════════════════════════════════════════════════
        const infoH = 18;
        fr(M, y, CW, infoH, BGROW);
        doc.setDrawColor(...BRDR); doc.setLineWidth(0.3);
        doc.line(M, y + infoH, M + CW, y + infoH);

        const infoFields = [
          { label: "INVOICE NO",      value: this.form.invoiceNumber || "-" },
          { label: "INVOICE DATE",    value: fmtDate(this.form.invoiceDate) },
          { label: "DUE DATE",        value: fmtDate(this.form.dueDate) },
          { label: "PLACE OF SUPPLY", value: this.form.placeOfSupply || "-" },
        ];
        const fw = CW / 4;
        infoFields.forEach((f, i) => {
          const fx = M + i * fw + (i === 0 ? 6 : 5);
          if (i > 0) {
            doc.setDrawColor(...BRDR); doc.setLineWidth(0.25);
            doc.line(M + i * fw, y + 2, M + i * fw, y + infoH - 2);
          }
          doc.setFontSize(6.5); doc.setFont("helvetica", "bold"); sc(...MUTED);
          doc.text(f.label, fx, y + 6.5);
          doc.setFontSize(9); doc.setFont("helvetica", "bold"); sc(...DARK);
          doc.text(String(f.value), fx, y + 14);
        });
        rc();
        y += infoH + 5;

        // ══════════════════════════════════════════════════════════════
        // 3. BILL FROM / BILL TO  — card style, blue label on light bg
        // ══════════════════════════════════════════════════════════════
        const colW  = (CW - 4) / 2;
        const LHDR  = 7;   // label header row height
        const CGAP  = 1.8;

        const sLines = [
          wt(seller.businessName || "-",          colW - PAD * 2),
          wt(seller.address      || "-",          colW - PAD * 2),
          wt(`Ph  ${seller.phone || "-"}`,        colW - PAD * 2),
          wt(seller.email        || "-",          colW - PAD * 2),
          wt(`GSTIN  ${seller.gstin || "-"}`,     colW - PAD * 2),
        ];
        const cLines = [
          wt(customer.name    || "-",             colW - PAD * 2),
          wt(customer.address || "-",             colW - PAD * 2),
          wt(`GSTIN  ${customer.gstin || "URP"}`, colW - PAD * 2),
        ];

        const totalTextH = (groups) =>
          groups.reduce((s, g) => s + g.length * LH + CGAP, 0);

        const fromH = LHDR + PAD + 1 + totalTextH(sLines) + PAD;
        const toH   = LHDR + PAD + 1 + totalTextH(cLines) + PAD;
        const billH = Math.max(fromH, toH);

        const fromX = M;
        const toX   = M + colW + 4;

        // draw card boxes
        fr(fromX, y, colW, billH, WHITE);
        sr(fromX, y, colW, billH);
        fr(fromX, y, colW, LHDR, BGROW);
        hl(fromX, fromX + colW, y + LHDR, BLIGHT);

        fr(toX, y, colW, billH, WHITE);
        sr(toX, y, colW, billH);
        fr(toX, y, colW, LHDR, BGROW);
        hl(toX, toX + colW, y + LHDR, BLIGHT);

        // label text in blue
        doc.setFontSize(7); doc.setFont("helvetica", "bold"); sc(...BLUE);
        doc.text("BILL FROM", fromX + PAD, y + LHDR - 2);
        doc.text("BILL TO",   toX   + PAD, y + LHDR - 2);
        rc();

        // seller content
        const selStyles = [
          { sz: 9,   style: "bold",   col: DARK },
          { sz: 7.5, style: "normal", col: BODY },
          { sz: 7,   style: "normal", col: LBODY },
          { sz: 7,   style: "normal", col: LBODY },
          { sz: 7.5, style: "normal", col: DARK },
        ];
        let fy = y + LHDR + PAD + 1;
        sLines.forEach((lines, i) => {
          const s = selStyles[i] || { sz: 7.5, style: "normal", col: BODY };
          fy = rl(lines, fromX + PAD, fy, s.sz, s.style, s.col) + CGAP;
        });

        // customer content
        const cusStyles = [
          { sz: 9,   style: "bold",   col: DARK },
          { sz: 7.5, style: "normal", col: BODY },
          { sz: 7.5, style: "normal", col: DARK },
        ];
        let cy2 = y + LHDR + PAD + 1;
        cLines.forEach((lines, i) => {
          const s = cusStyles[i] || { sz: 7.5, style: "normal", col: BODY };
          cy2 = rl(lines, toX + PAD, cy2, s.sz, s.style, s.col) + CGAP;
        });

        y += billH + 5;

        // ══════════════════════════════════════════════════════════════
        // 4. ITEMS TABLE
        // ══════════════════════════════════════════════════════════════
        const tHead = sameState
          ? ["#", "PRODUCT / DESCRIPTION", "HSN/SAC", "QTY", "RATE", "GST%", "CGST", "SGST", "AMOUNT"]
          : ["#", "PRODUCT / DESCRIPTION", "HSN/SAC", "QTY", "RATE", "GST%", "IGST", "AMOUNT"];

        const tBody = this.form.items.map((item, idx) => {
          const { cgst, sgst, igst } = splitTax({
            sellerState: this.sellerState,
            placeOfSupplyState: this.placeOfSupplyState,
            taxableAmount: item.amount || 0,
            taxPercent: item.taxPercent || 0,
          });
          const tax = cgst + sgst + igst;
          const row  = [
            idx + 1,
            item.product || "-",
            item.hsn || "-",
            item.quantity,
            this.formatINR(item.price),
            `${item.taxPercent || 0}%`,
          ];
          if (sameState) row.push(this.formatINR(cgst), this.formatINR(sgst));
          else           row.push(this.formatINR(igst));
          row.push(this.formatINR((item.amount || 0) + tax));
          return row;
        });

        const tColStyles = sameState
          ? [
              { halign: "center", cellWidth: 8 },   // #
              { halign: "left" },                    // Product (auto)
              { halign: "center", cellWidth: 22 },   // HSN/SAC
              { halign: "center", cellWidth: 14 },   // Qty
              { halign: "right",  cellWidth: 20 },   // Rate
              { halign: "center", cellWidth: 18 },   // GST%
              { halign: "right",  cellWidth: 18 },   // CGST
              { halign: "right",  cellWidth: 18 },   // SGST
              { halign: "right",  cellWidth: 22 },   // Amount
            ]
          : [
              { halign: "center", cellWidth: 8 },    // #
              { halign: "left" },                    // Product (auto)
              { halign: "center", cellWidth: 22 },   // HSN/SAC
              { halign: "center", cellWidth: 14 },   // Qty
              { halign: "right",  cellWidth: 24 },   // Rate
              { halign: "center", cellWidth: 18 },   // GST%
              { halign: "right",  cellWidth: 26 },   // GST
              { halign: "right",  cellWidth: 28 },   // Amount
            ];

        autoTable(doc, {
          startY: y,
          margin: { left: M, right: M },
          head: [tHead],
          body: tBody,
          theme: "grid",
          styles:             { fontSize: 8.5, overflow: "linebreak", cellPadding: 3, valign: "middle", textColor: DARK },
          headStyles:         { fillColor: NAVY, textColor: WHITE, fontStyle: "bold", fontSize: 7.5, halign: "center", cellPadding: 3.5 },
          alternateRowStyles: { fillColor: BGALT },
          bodyStyles:         { lineColor: BLIGHT, lineWidth: 0.25 },
          columnStyles:       Object.fromEntries(tColStyles.map((s, i) => [i, s])),
          didDrawPage:        (data) => { y = data.cursor.y + 4; },
          showHead:           "everyPage",
        });

        // ══════════════════════════════════════════════════════════════
        // 5. BANK + SUMMARY  — card style, no colored strip header
        // ══════════════════════════════════════════════════════════════
        const grandTotal = this.totalAmount;

        const totRows = sameState
          ? [["Subtotal", this.formatINR(this.subtotal)], ["CGST", this.formatINR(this.taxSplit.cgst)], ["SGST", this.formatINR(this.taxSplit.sgst)]]
          : [["Subtotal", this.formatINR(this.subtotal)], ["IGST", this.formatINR(this.taxSplit.igst)]];

        const TOT_W    = 80;
        const BANK_W   = CW - TOT_W - 4;
        const TOT_X    = M + CW - TOT_W;
        const BANK_X   = M;
        const ROW_H    = 7;
        const GRAND_H  = 10;

        const bankRows = [
          ["Account No", bank?.accountNumber || "-"],
          ["Bank",       bank?.bankName      || "-"],
          ["IFSC",       bank?.ifsc          || "-"],
          ["Branch",     bank?.branch        || "-"],
        ].filter(r => r[1] !== "-" || r[0] === "Account No");

        const sumH     = PAD + 2 + totRows.length * ROW_H + 2 + GRAND_H;
        const bankH    = LHDR + PAD + bankRows.length * ROW_H + PAD + 2;
        const sectionH = Math.max(sumH, bankH, 36);

        if (y + sectionH > pageH - FOOTER_H - 4) { doc.addPage(); y = M; }

        // bank card
        fr(BANK_X, y, BANK_W, sectionH, WHITE);
        sr(BANK_X, y, BANK_W, sectionH);
        fr(BANK_X, y, BANK_W, LHDR, BGROW);
        hl(BANK_X, BANK_X + BANK_W, y + LHDR, BLIGHT);
        doc.setFontSize(7); doc.setFont("helvetica", "bold"); sc(...BLUE);
        doc.text("BANK DETAILS", BANK_X + PAD, y + LHDR - 2);
        rc();

        let bankY = y + LHDR + PAD + 1;
        bankRows.forEach(([lbl, val], bi) => {
          if (bi > 0) hl(BANK_X + PAD, BANK_X + BANK_W - PAD, bankY - 1, BLIGHT);
          doc.setFontSize(7); doc.setFont("helvetica", "normal"); sc(...MUTED);
          doc.text(lbl, BANK_X + PAD, bankY + 2.5);
          doc.setFontSize(8.5); doc.setFont("helvetica", "bold"); sc(...DARK);
          doc.text(String(val), BANK_X + BANK_W - PAD, bankY + 2.5, { align: "right" });
          bankY += ROW_H;
        });
        rc();

        // summary card
        fr(TOT_X, y, TOT_W, sectionH, WHITE);
        sr(TOT_X, y, TOT_W, sectionH);

        let sumY = y + PAD + 2;
        totRows.forEach(([lbl, val], si) => {
          if (si > 0) hl(TOT_X + PAD, TOT_X + TOT_W - PAD, sumY - 1.5, BLIGHT);
          doc.setFontSize(8.5); doc.setFont("helvetica", "normal"); sc(...BODY);
          doc.text(lbl, TOT_X + PAD, sumY + 2.5);
          doc.setFont("helvetica", "normal"); sc(...DARK);
          doc.text(val, TOT_X + TOT_W - PAD, sumY + 2.5, { align: "right" });
          sumY += ROW_H;
        });

        const totalBarY = y + sectionH - GRAND_H;
        fr(TOT_X, totalBarY, TOT_W, GRAND_H, NAVY);
        doc.setFontSize(9); doc.setFont("helvetica", "bold"); sc(...WHITE);
        doc.text("TOTAL", TOT_X + PAD, totalBarY + GRAND_H / 2 + 1.8);
        doc.text(`INR ${this.formatINR(grandTotal)}`, TOT_X + TOT_W - PAD, totalBarY + GRAND_H / 2 + 1.8, { align: "right" });
        rc();

        y += sectionH + 4;

        // ══════════════════════════════════════════════════════════════
        // 6. AMOUNT IN WORDS
        // ══════════════════════════════════════════════════════════════
        const wordMaxW  = CW - PAD * 2 - 34;
        const wordsWrap = wt(this.numberToWords(Math.round(grandTotal)), wordMaxW);
        const wordsH    = Math.max(wordsWrap.length * 4.5, 4.5) + 9;

        if (y + wordsH > pageH - FOOTER_H - 4) { doc.addPage(); y = M; }

        fr(M, y, CW, wordsH, WBLU);
        sr(M, y, CW, wordsH, WBDR);
        const wordsTextY = y + 7;
        doc.setFontSize(7); doc.setFont("helvetica", "bold"); sc(90, 112, 153);
        doc.text("AMOUNT IN WORDS", M + PAD, wordsTextY);
        doc.setFontSize(8.5); doc.setFont("helvetica", "bolditalic"); sc(...DARK);
        wordsWrap.forEach((l, i) => doc.text(l, M + PAD + 38, wordsTextY + i * 4.5));
        rc();
        y += wordsH + 4;

        // ══════════════════════════════════════════════════════════════
        // 7. NOTES + TERMS  — card style, blue numbered terms
        // ══════════════════════════════════════════════════════════════
        const noteW  = (CW - 4) / 2;
        const termW  = (CW - 4) / 2;
        const noteX  = M;
        const termX  = M + noteW + 4;

        const noteWrap     = wt(this.form.note || "-", noteW - PAD * 2);
        const termTextW    = termW - PAD * 2 - 6;
        const termRendered = termEntries.map(e => wt(e, termTextW));
        const termTotalLines = termRendered.reduce((s, l) => s + l.length, 0);
        const ntBodyH = Math.max(noteWrap.length * 4.5, termTotalLines * 5, 10);
        const ntH     = LHDR + PAD + ntBodyH + PAD;

        if (y + ntH > pageH - FOOTER_H - 4) { doc.addPage(); y = M; }

        // notes card
        fr(noteX, y, noteW, ntH, WHITE);
        sr(noteX, y, noteW, ntH);
        fr(noteX, y, noteW, LHDR, BGROW);
        hl(noteX, noteX + noteW, y + LHDR, BLIGHT);
        doc.setFontSize(7); doc.setFont("helvetica", "bold"); sc(...BLUE);
        doc.text("NOTES", noteX + PAD, y + LHDR - 2);
        rl(noteWrap, noteX + PAD, y + LHDR + PAD + 1, 8, "normal", BODY, 4.5);

        // terms card
        fr(termX, y, termW, ntH, WHITE);
        sr(termX, y, termW, ntH);
        fr(termX, y, termW, LHDR, BGROW);
        hl(termX, termX + termW, y + LHDR, BLIGHT);
        doc.setFontSize(7); doc.setFont("helvetica", "bold"); sc(...BLUE);
        doc.text("TERMS & CONDITIONS", termX + PAD, y + LHDR - 2);
        rc();

        let tcy = y + LHDR + PAD + 3;
        termRendered.forEach((lines, ti) => {
          doc.setFontSize(8); doc.setFont("helvetica", "bold"); sc(...BLUE);
          doc.text(`${ti + 1}.`, termX + PAD, tcy);
          doc.setFont("helvetica", "normal"); sc(...BODY);
          lines.forEach((l, li) => doc.text(l, termX + PAD + 6, tcy + li * 4.5));
          tcy += lines.length * 4.5 + 2;
          rc();
        });

        y += ntH + 5;

        // ══════════════════════════════════════════════════════════════
        // 8. SIGNATURE  — right-aligned card
        // ══════════════════════════════════════════════════════════════
        const sigW  = 66;
        const sigH  = 26;
        const sigX  = M + CW - sigW;
        if (y + sigH > pageH - FOOTER_H - 4) { doc.addPage(); y = M; }
        const sigY  = y;

        fr(sigX, sigY, sigW, sigH, WHITE);
        sr(sigX, sigY, sigW, sigH);
        hl(sigX, sigX + sigW, sigY + sigH - 9, BRDR);

        doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); sc(...MUTED);
        doc.text(`For, ${seller.businessName || "-"}`, sigX + 5, sigY + 7);
        doc.setFontSize(7.5); doc.setFont("helvetica", "bold"); sc(...NAVY);
        doc.text("AUTHORISED SIGNATORY", sigX + sigW / 2, sigY + sigH - 2.5, { align: "center" });
        rc();

        // ══════════════════════════════════════════════════════════════
        // 9. FOOTER  (every page)
        // ══════════════════════════════════════════════════════════════
        const nPages = doc.internal.getNumberOfPages();
        for (let p = 1; p <= nPages; p++) {
          doc.setPage(p);
          fr(M, pageH - FOOTER_H, CW, FOOTER_H, NAVY);
          doc.setFontSize(7); doc.setFont("helvetica", "normal"); sc(...WHITE);
          doc.text("This is a computer generated invoice.", M + PAD, pageH - FOOTER_H + 6);
          doc.text(`Page ${p} of ${nPages}`, M + CW - PAD, pageH - FOOTER_H + 6, { align: "right" });
        }
        rc();

        const blob = doc.output("blob");
        this.preview = URL.createObjectURL(blob);
        this.file   = new File([blob], `invoice-${this.form.invoiceNumber}.pdf`, { type: "application/pdf" });
        this.pdfGenerated = true;
        this.$toast.success("Invoice Generated Successfully");
      } catch (e) {
        console.error(e);
        this.$toast.error("Invoice generation failed");
      }
    },

    onCustomerCreated(customer) {
      this.customerDialog = false;
      if (customer) {
        this.selectedCustomer = customer;
        this.onCustomerSelect(customer);
      }
    },

    openProductDialog(index) {
      this.activeItemIndex = index;
      this.productDialog = true;
    },

    onProductSelect(item, index) {
      const product = item.productObj;

      if (!product) return;

      item.productId = product._id;
      item.product = product.productName;
      item.hsn = product.hsn;
      item.price = product.rate;
      item.availableStock = product.trackStock ? product.stockQuantity : null;

      this.calcItem(index);
    },

    stockHint(item) {
      if (item.availableStock == null) return "";
      return this.isOverStock(item)
        ? `Only ${item.availableStock} in stock`
        : `${item.availableStock} in stock`;
    },

    isOverStock(item) {
      return (
        item.availableStock != null &&
        Number(item.quantity) > item.availableStock
      );
    },

    async onProductCreated(product) {
      this.productDialog = false;

      if (!product) return;

      const index = this.activeItemIndex;

      if (index === null || !this.form.items[index]) return;

      await this.fetchProducts();

      const item = this.form.items[index];

      const selected = this.products.find((p) => p._id === product._id);

      const finalProduct = selected || product;

      this.$set(item, "productObj", finalProduct);

      this.onProductSelect(item, index);

      this.activeItemIndex = null;
    },
  },
};
</script>

<style scoped>
.inv-qty-warning ::v-deep .v-messages__message {
  color: #b23a3a;
}

.inv-form-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.inv-form-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 20px;
  position: sticky;
  top: 0;
  z-index: 2;
}

.inv-form-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.inv-demo-btn.v-btn {
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.16) !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff !important;
  font-weight: 600 !important;
  text-transform: none !important;
  animation: inv-demo-pulse 2.2s ease-out infinite;
}

.inv-demo-btn.v-btn::before {
  border-radius: inherit;
}

.inv-demo-btn.v-btn::after {
  content: '';
  position: absolute;
  top: 0;
  left: -75%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    115deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.55) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: skewX(-20deg);
  animation: inv-demo-shine 2.6s ease-in-out infinite;
  pointer-events: none;
}

.inv-demo-btn.v-btn:hover {
  background: rgba(255, 255, 255, 0.26) !important;
  animation-play-state: paused;
}

.inv-demo-btn.v-btn:hover::after {
  animation-play-state: paused;
}

@keyframes inv-demo-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.45);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
}

@keyframes inv-demo-shine {
  0% {
    left: -75%;
  }
  40%,
  100% {
    left: 130%;
  }
}

.inv-form-actions {
  margin: 0;
  padding-top: 16px;
  padding-bottom: 16px;
  border-top: 1px solid #e5e9ef;
  background: #fff;
}

.inv-form-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.inv-form-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.inv-form-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.inv-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.inv-item-row {
  position: relative;
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 14px 56px 14px 12px;
  margin-bottom: 10px;
}

.inv-item-remove {
  position: absolute;
  bottom: 6px;
  right: 6px;
}

.inv-add-item-btn {
  background: #eef3fb !important;
  color: #0a5a90 !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.inv-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.inv-preview-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.inv-preview-header {
  display: flex;
  align-items: center;
  padding: 18px 20px 8px;
  flex-shrink: 0;
}

.inv-preview-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.inv-generate-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.preview-img {
  max-width: 100%;
}

.preview-frame {
  width: 100%;
  height: 100%;
  flex: 1;
}

.empty-state {
  color: #94a3b8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.viewer {
  flex: 1 1 auto;
  min-height: 0;
  margin-bottom: 16px;
  border: 1px dashed #cbd5e1;
  border-radius: 0;
  padding: 10px;
  background: #fafbfc;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.totals-grid {
  display: grid;
  grid-template-columns: 1fr auto;
  row-gap: 10px;
  column-gap: 40px;
  font-size: 14px;
  margin-top: 10px;
}

.total-row {
  display: flex;
  justify-content: space-between;
  font-size: 18px;
  border-top: 1px solid #ddd;
  padding-top: 12px;
  margin-top: 10px;
}

.v-file-input .v-file-input__text {
  overflow: visible !important;
  text-overflow: unset !important;
  white-space: normal !important;
}

.file-name-full {
  white-space: normal;
  word-break: break-all;
  line-height: 1.2;
}
</style>
